import React, { useEffect, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { useToast } from "../../components/ui";
import { FaTimes } from "react-icons/fa";
import { FiCreditCard } from "react-icons/fi";
import { RiCoupon2Line } from "react-icons/ri";
import {
  Header,
  UserPreview,
  OrderStatusBadge,
} from "../../components/modules";
import Context from "../../Context";
import { useParams } from "react-router-dom";
import {
  OrderBody,
  NoteAndSlips,
  OrderHistory,
  CouponHistory,
  OrderTimeline,
  OrderTransferModal,
  TransferReviewModal,
} from "./orderInfo/components";
import { ArrowRightLeft, ShieldCheck, RefreshCw } from "lucide-react";

export const OrderInfo = () => {
  const { toast } = useToast();
  const { orderId } = useParams();
  const { callApi, navigate, db, profileState } = Context();
  const [order, setOrder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(false);


  // Order Transfer State
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [pendingTransferRequest, setPendingTransferRequest] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  // Check if current staff can approve order transfers
  const currentRole = (profileState?.role || "").toLowerCase();
  const canApproveTransfer =
    currentRole === "dev" ||
    currentRole === "ceo" ||
    currentRole === "superadmin" ||
    profileState?.superAdmin ||
    profileState?.superSuperAdmin;

  useEffect(() => {
    if (!order?.hasPendingTransfer) {
      setPendingTransferRequest(null);
      return;
    }
    const fetchPendingReq = async () => {
      try {
        const res = await callApi(
          "orders-getTransferRequests",
          { status: "pending", limit: 50 },
          { notShowLoading: true }
        );
        if (res?.status && Array.isArray(res.data)) {
          const found = res.data.find(
            (r) => r.orderId === order.id || r.id === order.pendingTransferId
          );
          setPendingTransferRequest(found || null);
        }
      } catch (e) {
        console.warn("fetch pending transfer in orderInfo error:", e);
      }
    };
    fetchPendingReq();
  }, [order?.hasPendingTransfer, order?.id, order?.pendingTransferId]);


  // Record admin viewing/working on this order into order.working
  const recordedWorkingRef = useRef(false);

  useEffect(() => {
    recordedWorkingRef.current = false;
  }, [orderId]);

  useEffect(() => {
    if (!order || order.id !== orderId || !profileState || recordedWorkingRef.current) return;

    const adminId = profileState?.userId
      ? String(profileState.userId)
      : profileState?.username
      ? String(profileState.username)
      : profileState?._id
      ? String(profileState._id)
      : null;

    if (!adminId) return;

    const rawName =
      profileState?.displayName ||
      [profileState?.firstName, profileState?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      profileState?.name ||
      profileState?.username ||
      "แอดมิน";
    const firstName = profileState?.firstName || profileState?.displayName || rawName;
    const profileImage = profileState?.profileImage || "";

    const adminEntry = {
      id: adminId,
      name: rawName,
      firstName,
      profileImage,
      viewedAt: Date.now(),
    };

    const existingWorking = Array.isArray(order.working)
      ? order.working
      : order.working
      ? [order.working]
      : [];

    const isAlreadyWorking = existingWorking.some((item) => {
      const id = typeof item === "string" ? item : item?.id || item?.userId;
      return id === adminId;
    });

    recordedWorkingRef.current = true;

    if (!isAlreadyWorking) {
      const updatedWorking = [...existingWorking, adminEntry];
      callApi(
        "orders-edit",
        {
          id: order.id,
          key: "working",
          value: updatedWorking,
        },
        { notShowLoading: true }
      ).catch((err) => {
        console.warn("Could not save working admin to order:", err);
      });
    }
  }, [order, orderId, profileState, callApi]);

  useEffect(() => {
    setOrder(false);
    setLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, "Orders", orderId),
      (snapshot) => {
        if (!snapshot.exists()) {
          toast({
            className: "bg-rose-600 text-white font-semibold",
            title: (
              <div className="flex gap-2 items-center">
                <FaTimes /> ไม่พบข้อมูลคำสั่งซื้อ
              </div>
            ),
          });
          navigate("/orders");
          return;
        }
        const data = { ...snapshot.data(), id: snapshot.id };
        setOrder(data);
        setLoading(false);
      },
      (error) => {
        console.error("onSnapshot error:", error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    let active = true;
    setUser(false);
    if (!order?.userId || order.id !== orderId) return;

    const getUser = async () => {
      try {
        const { status, data } = await callApi(
          "users-getUserInfoAdmin",
          { userId: order.userId },
          { notShowLoading: true }
        );
        if (!active) return;
        if (status) {
          setUser(data);
        } else {
          toast({
            className: "bg-rose-600 text-white font-semibold",
            title: "ไม่พบข้อมูลผู้ใช้งาน",
          });
        }
      } catch (error) {
        if (active) console.error("OrderInfo user loading error:", error);
      }
    };
    getUser();
    return () => { active = false; };
    // API/UI helpers may change identity; reload only when the order or owner changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, order?.id, order?.userId]);

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-10 min-w-0">
      {/* Header */}
      <Header
        header="Orders"
        subHeader={`คำสั่งซื้อ / ${orderId}`}
        icon={<FiCreditCard className="text-blue-600" />}
      />

      {/* ข้อมูลลูกค้า */}
      <div className="mt-4">
        <UserPreview user={user} />
      </div>

      {/* Banner แจ้งเตือนคำขอย้ายคำสั่งซื้อที่รอดำเนินการ */}
      {order?.hasPendingTransfer && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-300 rounded-md flex flex-wrap items-center justify-between gap-3 shadow-2xs animate-in fade-in-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500 text-white rounded-md shrink-0">
              <RefreshCw className="w-5 h-5 animate-spin" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-amber-950">
                คำสั่งซื้อนี้มีคำขอย้ายเจ้าของที่รอดำเนินการอนุมัติ
              </h4>
              <p className="text-xs text-amber-850 font-medium mt-0.5">
                {pendingTransferRequest?.toUser?.phone
                  ? `ขอย้ายไปยังเบอร์: ${pendingTransferRequest.toUser.phone} (${pendingTransferRequest.toUser.firstName || "ลูกค้า"}) • โดย ${pendingTransferRequest?.requestedBy?.name || "แอดมิน"}`
                  : "กำลังรอการตรวจสอบหลักฐานและอนุมัติจาก Dev หรือ Team Lead Admin"}
              </p>
            </div>
          </div>
          {canApproveTransfer && (
            <button
              type="button"
              onClick={() => setIsReviewModalOpen(true)}
              className="py-2 px-3 text-xs font-bold rounded-md bg-amber-600 hover:bg-amber-700 text-white shadow-xs transition cursor-pointer flex items-center gap-1.5 shrink-0"
            >
              <ShieldCheck className="w-4 h-4" />
              <span>ตรวจสอบและจัดการคำขอนี้</span>
            </button>
          )}
        </div>
      )}

      {/* ลำดับสถานะคำสั่งซื้อ, ประวัติคำสั่งซื้อในงวด, และคูปองของลูกค้า */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4 items-stretch">
        <OrderTimeline orderInfo={order} />
        <OrderHistory order={order} />
        <CouponHistory user={user} />
      </div>

      {/* การ์ดหลักแสดงรายละเอียดคำสั่งซื้อ */}
      <div className="relative bg-white w-full rounded-md shadow-xs mt-4 border border-slate-200 overflow-hidden">
        <div className="flex bg-slate-50/70 items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <h2 className="font-bold text-xl text-slate-800">
              ข้อมูลคำสั่งซื้อ {orderId?.slice(-5)?.toUpperCase() || ""}
            </h2>
            {order?.status && <OrderStatusBadge status={order.status} />}
            {Boolean(
              order?.isReward ||
                order?.missionOrder ||
                order?.missionRef ||
                order?.coupon?.id ||
                order?.isReserveOrder ||
                order?.isUsePromoCode ||
                order?.promoCode ||
                order?.couponDiscount ||
                order?.promoDiscount
            ) && (
              <>
                {order?.isReward || order?.missionOrder ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-md bg-blue-50 text-blue-700 border border-blue-300 shadow-2xs">
                    <span className="text-sm">🎯</span>
                    <span>
                      รางวัลภารกิจ
                      {order?.missionOrder
                        ? ` #${order.missionOrder.slice(-5).toUpperCase()}`
                        : ""}
                    </span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-md bg-emerald-50 text-emerald-700 border border-emerald-300 shadow-2xs">
                    <RiCoupon2Line className="text-sm shrink-0" />
                    <span>
                      {order?.isReserveOrder
                        ? "คูปองจองสิทธิ์"
                        : order?.coupon?.coupon?.name ||
                          order?.coupon?.name ||
                          (order?.promoCode
                            ? `โค้ด: ${order.promoCode}`
                            : "ใช้คูปองส่วนลด")}
                    </span>
                    {!order?.isReserveOrder &&
                      order?.coupon?.coupon?.type !== "voucher" &&
                      (Number(order?.promoDiscount || 0) > 0 ||
                        Number(order?.couponDiscount || 0) > 0) && (
                        <span className="text-emerald-800 font-extrabold">
                          (
                          -
                          {(
                            order.promoDiscount || order.couponDiscount
                          ).toLocaleString()}{" "}
                          บ.)
                        </span>
                      )}
                  </span>
                )}
              </>
            )}
          </div>

          {/* Action: ขอย้ายเจ้าของคำสั่งซื้อ */}
          <button
            type="button"
            disabled={order?.hasPendingTransfer || ["cancelled", "expired"].includes(order?.status)}
            onClick={() => setIsTransferModalOpen(true)}
            className={`px-3 py-1.5 text-xs font-bold rounded-md border transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs ${
              order?.hasPendingTransfer
                ? "bg-amber-50 text-amber-800 border-amber-300 opacity-90 cursor-default"
                : "bg-white text-slate-700 border-slate-300 hover:border-blue hover:text-blue hover:bg-blue/5"
            }`}
            title={order?.hasPendingTransfer ? "มีคำขอย้ายรอดำเนินการอยู่" : "ขอย้ายเจ้าของคำสั่งซื้อสลาก"}
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
            <span>{order?.hasPendingTransfer ? "มีคำขอย้ายรอดำเนินการ" : "ขอย้ายเจ้าของคำสั่งซื้อ"}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 p-4">
          {loading ? (
            <div className="w-full col-span-3 border-r border-slate-200 z-50 h-full min-h-[300px] bg-white/70 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <OrderBody order={order} user={user} />
          )}
          <NoteAndSlips order={order} />
        </div>
      </div>

      {/* Modal ยื่นคำขอย้ายคำสั่งซื้อ */}
      <OrderTransferModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        order={order}
        currentUser={user}
        onSuccess={() => {
          toast({
            className: "bg-emerald-600 text-white font-semibold py-2 px-3 rounded-md",
            title: "ส่งคำขอย้ายคำสั่งซื้อเรียบร้อยแล้ว",
          });
        }}
      />

      {/* Modal ตรวจสอบและอนุมัติ (สำหรับผู้อนุมัติ) */}
      {pendingTransferRequest && (
        <TransferReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          request={pendingTransferRequest}
          onSuccess={() => {
            setIsReviewModalOpen(false);
          }}
        />
      )}
    </div>
  );
};
